/**
 * Pure ownership helpers for on-demand admin public-response fetches.
 * Prevents stale mailing A results from overwriting mailing B (or a closed panel).
 */

export function createAdminPublicResponseRequestTracker() {
  let latestRequestId = 0;
  return {
    begin({ campaignId, mailingId }) {
      const requestId = ++latestRequestId;
      return {
        requestId,
        campaignId: typeof campaignId === 'string' ? campaignId : '',
        mailingId: typeof mailingId === 'string' ? mailingId : '',
      };
    },
    invalidate() {
      latestRequestId += 1;
      return latestRequestId;
    },
    isCurrent(owner) {
      return Boolean(owner && owner.requestId === latestRequestId);
    },
    get latestRequestId() {
      return latestRequestId;
    },
  };
}

/**
 * Decide whether an async admin response result may update UI state.
 */
export function shouldApplyAdminPublicResponseResult({
  owner,
  tracker,
  mounted,
  panel,
  activeCampaignId,
}) {
  if (!mounted || !owner || !tracker?.isCurrent(owner)) return false;
  if (!panel || panel.status === 'closed') return false;
  if (!activeCampaignId || owner.campaignId !== activeCampaignId) return false;
  if (!panel.mailingId || owner.mailingId !== panel.mailingId) return false;
  if (panel.campaignId && panel.campaignId !== owner.campaignId) return false;
  return true;
}

export function createClosedAdminPublicResponsePanel() {
  return { status: 'closed', campaignId: null, mailingId: null, responses: [] };
}

export function createLoadingAdminPublicResponsePanel({ campaignId, mailingId }) {
  return {
    status: 'loading',
    campaignId,
    mailingId,
    responses: [],
  };
}

/**
 * Whether the response panel may be scrolled/focused into view after open.
 * Only the request that opened the panel may reveal it; stale/invalidated opens must not.
 */
export function shouldRevealAdminPublicResponsePanel({ owner, tracker, mounted, panel }) {
  if (!mounted || !owner || !tracker?.isCurrent(owner)) return false;
  if (!panel || panel.status === 'closed') return false;
  if (owner.campaignId !== panel.campaignId || owner.mailingId !== panel.mailingId) return false;
  return true;
}

/** Focus + scroll a response panel element into view (no-op without a DOM node). */
export function revealAdminPublicResponsePanelElement(element) {
  if (!element || typeof element.focus !== 'function') return false;
  element.focus({ preventScroll: true });
  if (typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
  return true;
}
